// Basic
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
// Store & actions
import actionsOrganizations from '../../api/actionsOrganizations';
import useSessionStore from '@/app/store/session';
import useOrganizationsStore from '@/app/store/modules/organizations';
// Components
import Entity from '@/common-utils/components/renders/entity/Entity';
import ModalDeleteEmail from '../modals/alerts/ModalDeleteEmail';
import { Skeleton } from '@/common-utils/components/ui/skeleton';
import { Button } from '@/common-utils/components/ui/button';
// Common
import { useToast } from '@/common-utils/hooks/use-toast';
import ICONS from '@/common-utils/enums/icons';
import hasRequiredPermissions from '@/common-utils/functions/hasRequiredPermissions';
import Permissions from '@/app/permissions/Permissions';
// Lang
import { nsOrganization } from '@/app/i18n/namespaces';

/**
 * @returns {JSX} SimCardsConsumptionSection
 */
function SimCardsConsumptionSection() {

    // URL Params
    const { organizationId } = useParams();

    // Toast
    const { toast } = useToast();

    // Store
    const { permissions: userPermissions } = useSessionStore(s => s.session) ?? {};
    const { refetchSimAlerts, storeRefetchSimAlerts, storeSimAlerts } = useOrganizationsStore();

    // State
    const [simCardThreshold, setSimCardThreshold] = useState({});
    const [simPoolThreshold, setSimPoolThreshold] = useState({});
    const [mailList, setMailList] = useState([]);
    const [email, setEmail] = useState('');
    const [showModalDeleteEmail, setShowModalDeleteEmail] = useState(false);
    const [launchRefetch, setLaunchRefetch] = useState(false);

    // Api calls
    const { data, isFetching, refetch } = actionsOrganizations.getSimAlerts(organizationId, true);

    const onSuccess = () => {
        setShowModalDeleteEmail(false);
        setLaunchRefetch(true);
        toast({
            description: <Entity namespace={nsOrganization} entity='modal.delete_mail.success' />,
            variant: 'success'
        });
    };
    const onError = () => {
        setShowModalDeleteEmail(false);
        toast({
            description: <Entity namespace={nsOrganization} entity='modal.errors.UNKNOWN_ERROR' />,
            variant: 'danger'
        });
    };
    const deleteSimAlertReceivers = actionsOrganizations.deleteSimAlertReceivers(onSuccess, onError);


    // Hooks
    useEffect(() => {
        if (data) {
            setSimCardThreshold({ threshold: data.simCardThreshold });
            setSimPoolThreshold({ threshold: data.simPoolThreshold });
            setMailList(data.mailList);
            storeSimAlerts(data);
        }
    }, [data]);

    useEffect(() => {
        if (organizationId) {
            refetch();
            setLaunchRefetch(false);
            storeRefetchSimAlerts(false);
        }
    }, [launchRefetch, refetchSimAlerts]);

    // Functions
    const handleShowModalDeleteEmail = (item) => {
        setShowModalDeleteEmail(true);
        setEmail(item);
    };

    const handleDeleteEmail = () => {
        deleteSimAlertReceivers.mutate({
            organizationId: organizationId,
            emails: { mail: email }
        });
    };

    return (
        <div className='space-y-4'>
            <div className='w-full'>
                <div className='grid grid-cols-1 md:grid-cols-[auto,1fr] gap-y-4 gap-x-3'>
                    <div className='text-labelGray-foreground'>
                        <Entity
                            entity='details.alerts.consumption.sim_cards.title'
                            namespace={nsOrganization}
                        />
                    </div>
                    <div>
                        {isFetching && (
                            <div className='animate-pulse'>
                                <Skeleton className='w-48 rounded-full h-6' />
                            </div>
                        )}
                        {!isFetching && data && (
                            <Entity
                                className='text-sm font-medium'
                                entity={
                                    data.isActiveSimCardAlert
                                        ? 'details.alerts.consumption.sim_cards.configured'
                                        : 'details.alerts.consumption.sim_cards.non_configured'
                                }
                                namespace={nsOrganization}
                                data={simCardThreshold}
                            />
                        )}
                    </div>
                    <div className='text-labelGray-foreground'>
                        <Entity
                            entity='details.alerts.consumption.sim_groups.title'
                            namespace={nsOrganization}
                        />
                    </div>
                    <div>
                        {isFetching && (
                            <div className='animate-pulse'>
                                <Skeleton className='w-48 rounded-full h-6' />
                            </div>
                        )}

                        {!isFetching && data && (
                            <Entity
                                className='text-sm font-medium'
                                entity={
                                    data.isActiveSimPoolAlert
                                        ? 'details.alerts.consumption.sim_groups.configured'
                                        : 'details.alerts.consumption.sim_groups.non_configured'
                                }
                                namespace={nsOrganization}
                                data={simPoolThreshold}
                            />
                        )}
                    </div>
                </div>
            </div>
            <div className='space-y-2'>
                <Entity
                    className='text-labelGray-foreground'
                    entity='details.alerts.consumption.receivers'
                    namespace={nsOrganization}
                    data={{ count: mailList.length }}
                />
                {isFetching &&
                    <div className='animate-pulse flex flex-wrap gap-4'>
                        {[1, 2, 3].map((item) => (
                            <Skeleton key={item} className='w-48 rounded-full h-8' />
                        ))}
                    </div>
                }
                {!isFetching && data &&
                    <div className='flex flex-wrap gap-4'>
                        {mailList.map((mailAdress, index) => (
                            <div
                                key={`${mailAdress}-${index}`}
                                className='py-1 pl-1 pr-2 rounded-full border border-gray-200 bg-secondary-hover flex items-center'
                            >
                                <Button
                                    variant='critical'
                                    icon={ICONS.CLOSE}
                                    dataCy={`delete-sim-alert-mail-${index}`}
                                    onClick={() => handleShowModalDeleteEmail(mailAdress)}
                                    className='mr-2 p-0 h-6 w-6 rounded-full bg-secondary-hover'
                                    disabled={!hasRequiredPermissions({
                                        permissions: userPermissions,
                                        requiredPermissions: [
                                            Permissions.ORGANIZATION_UPDATE_ALERT_SIMS.name
                                        ]
                                    })}
                                />
                                <Entity
                                    className='text-sm font-medium'
                                    entity={mailAdress}
                                />
                            </div>
                        ))}
                    </div>
                }
            </div>
            {showModalDeleteEmail &&
                <ModalDeleteEmail
                    isOpen={showModalDeleteEmail}
                    setIsOpen={setShowModalDeleteEmail}
                    handleValidate={handleDeleteEmail}
                    isLoading={deleteSimAlertReceivers.isLoading}
                    email={email}
                />
            }
        </div>
    );
}

export default SimCardsConsumptionSection;